import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { agentApi, type Message } from '../../../../api/visitorsAgent'
import { btnPrimary, card, ErrorLine, h2, inp } from '../../../../campaigns-desk/ui'
import { errorDetail } from '../../../../lib/visitorsFormat'
import { isPending, safeDeskHref } from '../../../../lib/visitorsAgent'
import { Md } from './Md'

function Bubble({ m }: { m: Message }) {
  const mine = m.role === 'user'
  return (
    <div className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-[92%] space-y-2 rounded-xl px-3 py-2 ${mine ? 'bg-ink-600 text-paper' : 'border border-ink-500 bg-ink-800'}`}>
        {mine ? <p className="whitespace-pre-wrap break-words text-[14px]">{m.body}</p>
          : isPending(m.status) ? <p role="status" className="text-[13px] text-paper-faint">Thinking…</p>
          : m.status === 'failed' ? <p role="alert" className="text-[13px] text-radar-red">{m.error || 'The analyst could not answer.'}</p>
          : <Md text={m.body} />}
        {!mine && m.status === 'done' && m.citations.length > 0 && (
          <ul className="flex flex-wrap gap-x-3 gap-y-1 text-[12px]">
            {m.citations.map((c, i) => {
              const h = safeDeskHref(c.href)
              return <li key={i}>{h ? <Link to={h} className="text-amber-glow underline underline-offset-2">{c.label}</Link> : <span className="text-paper-faint">{c.label}</span>}</li>
            })}
          </ul>
        )}
      </div>
    </div>
  )
}

const STALE_MS = 10 * 60 * 1000
function pendingAge(msgs: Message[]): number | null {
  const m = msgs.find((x) => x.role === 'assistant' && isPending(x.status))
  return m ? Date.now() - new Date(m.created_at ?? 0).getTime() : null
}

/** One conversation: messages oldest first, polled every 3s while the analyst is answering. */
export function ChatPanel({ scope, conversationId, onOpen }: { scope: string; conversationId: number | null; onOpen: (id: number | null) => void }) {
  const qc = useQueryClient()
  const [text, setText] = useState('')
  const endRef = useRef<HTMLDivElement>(null)
  const conv = useQuery({
    queryKey: ['vd-agent-conversation', conversationId],
    queryFn: () => agentApi.conversation(conversationId as number),
    enabled: conversationId != null,
    select: (d) => ({ ...d, stale: (pendingAge(d.messages) ?? 0) > STALE_MS }),
    refetchInterval: (q) => {
      if (q.state.status === 'error' || !q.state.data) return false
      const age = pendingAge(q.state.data.messages)
      return age != null && age <= STALE_MS ? 3000 : false
    },
  })
  const msgs = conv.data?.messages ?? []
  const stale = conv.data?.stale ?? false
  const waiting = !stale && msgs.some((m) => m.role === 'assistant' && isPending(m.status))
  useEffect(() => { endRef.current?.scrollIntoView?.({ block: 'nearest' }) }, [msgs.length, waiting])

  const refresh = (id: number) => {
    void qc.invalidateQueries({ queryKey: ['vd-agent-conversation', id] })
    void qc.invalidateQueries({ queryKey: ['vd-agent-conversations', scope] })
  }
  const send = useMutation({
    mutationFn: async (body: string) => {
      let id = conversationId
      if (id == null) id = (await agentApi.createConversation({ scope })).id
      await agentApi.send(id, body)
      return id
    },
    onSuccess: (id) => { setText(''); if (id !== conversationId) onOpen(id); refresh(id) },
    onError: () => { if (conversationId != null) refresh(conversationId) },
  })
  const busy = send.isPending || waiting
  const submit = () => {
    const body = text.trim()
    if (!body || busy) return
    send.mutate(body)
  }

  return (
    <section aria-label="Ask the analyst" className={`${card} flex flex-col gap-3`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className={h2}>Ask the analyst</h2>
        {conversationId != null && <button type="button" className="text-[13px] text-amber-glow underline underline-offset-2" onClick={() => onOpen(null)}>New conversation</button>}
      </div>
      {conv.data && <p className="text-[13px] font-semibold text-paper">{conv.data.title}</p>}
      <div className="flex max-h-[60vh] min-h-[120px] flex-col gap-2 overflow-y-auto" aria-live="polite">
        {conversationId == null && <p className="text-[13px] text-paper-faint">Ask about visitors, sign-ups or what changed. Answers use counts only.</p>}
        {conv.isError && <ErrorLine text={errorDetail(conv.error)} />}
        {msgs.map((m) => <Bubble key={m.id} m={m} />)}
        {stale && <p role="alert" className="text-[13px] text-radar-red">The analyst hasn't answered yet — try again later.</p>}
        <div ref={endRef} />
      </div>
      <form className="flex flex-col gap-2" onSubmit={(e) => { e.preventDefault(); submit() }}>
        <textarea aria-label="Your question" rows={3} maxLength={2000} className={`${inp} w-full py-2`} value={text}
          placeholder="Ask a question" onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); submit() } }} />
        <div className="flex items-center justify-between gap-2">
          <span className="text-[12px] text-paper-faint">{text.length}/2000</span>
          <button type="submit" className={btnPrimary} disabled={busy || !text.trim()}>{busy ? 'Waiting…' : 'Send'}</button>
        </div>
        <ErrorLine text={send.isError ? errorDetail(send.error) : null} />
      </form>
    </section>
  )
}
